// LockedGameModal — opens when a player taps a locked game card on
// the lobby.
//
// Default UX: just informs the player they need to reach Level 3.
//
// Easter egg (Dark Launch sprint): tap the padlock icon 5 times within
// 1.5 seconds to reveal a hidden Developer PIN input + "Force Launch"
// button. Successful PIN verification grants 1-hour dev access to the
// chosen game and navigates straight into the gameplay (either the
// real Game Page for the 5 live games or the DevSandbox placeholder
// for the 10 still-under-construction games).
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import Icon from '@baaligames/shared-ui/components/Icon';
import type { LobbyGame } from '../lib/lobbyGames';
import { lobbyCache } from '../lib/lobbyGames';

type Props = {
  game: LobbyGame;
  onClose: () => void;
};

const TAP_TARGET = 5;
const TAP_WINDOW_MS = 1500;

export default function LockedGameModal({ game, onClose }: Props) {
  const nav = useNavigate();
  const { push } = useToast();

  // 5-tap state. We use a ref for the last-tap timestamp so we don't
  // re-render every time the timestamp updates, and a state count so
  // the reveal flips on the 5th tap.
  const [tapCount, setTapCount] = useState(0);
  const lastTapRef = useRef<number>(0);

  // Backdoor state — only relevant once the easter egg fires.
  const [pinInput, setPinInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  const revealed = tapCount >= TAP_TARGET;

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Tap handler — counts taps within the 1.5 s window. Once the target
  // is reached, the count locks in (no off-by-one if the user keeps
  // tapping after the reveal). A tap outside the window resets to 1
  // (treated as the first tap of a new sequence) so a slow tapper
  // doesn't compound their misses.
  const handlePadlockTap = useCallback(() => {
    if (revealed) return;
    const now = Date.now();
    setTapCount((prev) => {
      const within = now - lastTapRef.current <= TAP_WINDOW_MS;
      lastTapRef.current = now;
      return within ? prev + 1 : 1;
    });
  }, [revealed]);

  const pinValid = /^\d{4,8}$/.test(pinInput);

  async function submitPin() {
    if (!pinValid || verifying) return;
    setVerifying(true);
    setVerifyErr(null);
    try {
      await api.post<{ gameId: string; expiresInSec: number }>(
        '/me/games/verify-dev-pin',
        { gameId: game.id, pin: pinInput },
      );
      // Refresh the lobby cache so myDevGrants now includes this game.
      // The DevSandbox / GameRoute landing page reads from the same
      // cache to confirm access before mounting.
      await lobbyCache.refresh().catch(() => { /* non-fatal; server is still the authority */ });
      push('success', `Developer access granted — opening ${game.displayName}…`);
      onClose();
      // Tiny delay so the toast renders before the route switch yanks
      // the user out of the lobby.
      setTimeout(() => nav(`/games/${game.id}`), 80);
    } catch (e) {
      const err = e as ApiError;
      // Order matters — the 401 path covers both "wrong PIN" (server
      // sends code 'invalid_pin') and "session expired" (code
      // 'unauthorized'). Surface them separately so the dev knows
      // whether to retype the PIN or sign back in.
      if (err.status === 429) {
        setVerifyErr('Too many attempts. Try again in 15 minutes.');
      } else if (err.code === 'invalid_pin') {
        setVerifyErr('Invalid PIN.');
      } else if (err.status === 401) {
        setVerifyErr('Session expired — sign in again.');
      } else if (err.code === 'validation_error') {
        setVerifyErr('PIN must be 4–8 digits.');
      } else {
        setVerifyErr(err.message || 'Verification failed.');
      }
    } finally {
      setVerifying(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────
  //
  // Portaled to <body> so it floats above the lobby's stacking context
  // (the lobby card holding the click target uses backdrop-blur, which
  // creates a stacking context that traps `position: fixed` children
  // — same issue we hit with the proof image viewer).
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="locked-modal-title"
    >
      <div
        className="relative w-full max-w-sm rounded-3xl p-6
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button — top right. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full
                     text-arena-500 dark:text-neon-200/60
                     hover:bg-arena-100 dark:hover:bg-arena-700/60 transition"
        >
          <Icon name="close" size={18} />
        </button>

        {/* Padlock icon — TAP TARGET for the 5-tap easter egg. Uses an
            inline CSS gradient (not a Tailwind class) because the
            game's theme gradient is stored in the DB as a Tailwind
            class string and may not be in the compiled bundle when
            the DB carries a class the JIT scan didn't see. Hardcoded
            indigo→violet gradient keeps the padlock readable across
            every locked game and stays visually consistent with the
            Force Launch button below. */}
        <button
          type="button"
          onClick={handlePadlockTap}
          aria-label="Locked"
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%)',
          }}
          className={`mx-auto grid place-items-center w-20 h-20 rounded-full
                      text-white shadow-xl
                      transition active:scale-95
                      ${!revealed ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <Icon name={revealed ? 'lock_open' : 'lock'} size={36} filled />
        </button>

        {/* Game title + body copy. */}
        <h2
          id="locked-modal-title"
          className="mt-4 text-center text-xl font-extrabold text-arena-900 dark:text-white"
        >
          {game.displayName}
        </h2>
        <p className="mt-1 text-center text-[13px] text-arena-600 dark:text-neon-200/75 leading-snug">
          {revealed
            ? 'Developer access — enter the universal PIN.'
            : `Keep playing to reach Level 3 to unlock ${game.displayName}.`}
        </p>
        {/* Dark Launch (2026-05-22) — the "coming soon, we're polishing
            this game" line was deliberately removed. Players should
            read this modal as a normal level-gated progression message,
            not as "this game doesn't exist yet". The illusion of a
            full catalogue is the whole point of the dark launch. */}

        {/* Backdoor — hidden by default, revealed at 5 taps. */}
        {revealed && (
          <div className="mt-5 space-y-2">
            <label className="text-[12px] font-semibold text-arena-700 dark:text-neon-200/85">
              Developer PIN
            </label>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                maxLength={8}
                value={pinInput}
                onChange={(e) => {
                  setPinInput(e.target.value.replace(/\D/g, '').slice(0, 8));
                  setVerifyErr(null);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); }}
                placeholder="• • • • • •"
                className="w-full rounded-xl px-3 py-2 text-center font-mono text-lg tracking-[.4em]
                           bg-arena-50 dark:bg-arena-900/60
                           border border-arena-200/60 dark:border-arena-600/50
                           text-arena-900 dark:text-white
                           placeholder-arena-300 dark:placeholder-neon-200/35
                           focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400"
              />
              <button
                type="button"
                onClick={submitPin}
                disabled={!pinValid || verifying}
                className="inline-flex items-center justify-center gap-1.5
                           rounded-xl px-4 py-2.5 text-sm font-bold uppercase tracking-wider
                           text-white
                           bg-gradient-to-b from-violet-500 to-violet-700
                           hover:from-violet-400 hover:to-violet-600
                           shadow-[0_4px_14px_rgba(139,92,246,0.4)]
                           disabled:opacity-50 disabled:cursor-not-allowed
                           active:scale-[.98] transition"
              >
                {verifying ? (
                  <><Icon name="progress_activity" size={16} className="animate-spin" /> Verifying…</>
                ) : (
                  <><Icon name="bolt" size={16} filled /> Force Launch</>
                )}
              </button>
            </div>
            {verifyErr && (
              <p className="text-[12px] text-rose-700 dark:text-rose-300 text-center">
                {verifyErr}
              </p>
            )}
          </div>
        )}

        {/* "Got it" CTA for the default (non-dev) case. */}
        {!revealed && (
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-bold
                       text-arena-800 dark:text-neon-100
                       bg-arena-100 dark:bg-arena-700/60
                       hover:bg-arena-200 dark:hover:bg-arena-700 transition"
          >
            Got it
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
